/**
 * What the customer reads when the engine refuses a plan change, and what the
 * screen offers next. The engine answers `{ statusCode, message }`; its message
 * carries a stable code (`error_code` or a `PLAN_CHANGE_*` prefix, engine #305)
 * or, from an older engine, one of the guard's sentences in plan_change.go, so
 * every refusal is matched by its code or that fragment. A
 * refusal nobody mapped falls back to the generic copy, never to the raw text.
 */

export type PlanChangeNextStep =
	/** Quote again: the money or the plan moved meanwhile. */
	| 'requote'
	/** Try the same payment again in a moment. */
	| 'retry'
	/** Support does this change by hand. */
	| 'support'
	/** Nothing to do from here: the customer fixes something else first. */
	| 'none';

export interface PlanChangeRefusal {
	code: string;
	title: string;
	description: string;
	next: PlanChangeNextStep;
}

type RefusalEntry = PlanChangeRefusal & {
	match: string | string[];
	/** Show the engine's own sentence as the description (when it sends one). */
	engineReason?: boolean;
};

const REFUSALS: RefusalEntry[] = [
	{
		match: 'PLAN_CHANGE_NOT_ENABLED',
		code: 'not_enabled',
		title: 'Todavía no puedes cambiar de plan aquí',
		description: 'Escríbenos y hacemos el cambio por ti.',
		next: 'support',
	},
	{
		match: 'PLAN_CHANGE_QUOTE_CHANGED',
		code: 'quote_changed',
		title: 'El monto cambió',
		description: 'Mientras lo revisabas cambió lo que pagas. Revisa el monto nuevo antes de seguir.',
		next: 'requote',
	},
	{
		match: 'cambió mientras tanto',
		code: 'subscription_changed',
		title: 'Tu plan cambió',
		description: 'Tu plan cambió mientras lo revisabas. Revisa el cambio de nuevo.',
		next: 'requote',
	},
	{
		match: 'PLAN_CHANGE_DOWNGRADE_AT_RENEWAL_ONLY',
		code: 'downgrade_at_renewal_only',
		title: 'Este cambio es en tu renovación',
		description: 'Cambiar a un plan más barato se hace en tu renovación, no hoy. Revisa el cambio de nuevo.',
		next: 'requote',
	},
	{
		match: 'PLAN_CHANGE_RENEWAL_IN_PROGRESS',
		code: 'renewal_in_progress',
		title: 'Tu renovación se está procesando',
		description: 'Tu renovación ya se está procesando. Podrás cambiar de plan en cuanto se pague.',
		next: 'none',
		engineReason: true,
	},
	{
		match: 'PLAN_CHANGE_TIMING_NOT_ALLOWED',
		code: 'timing_not_allowed',
		title: 'Este cambio se aplica hoy',
		description: 'Subir de plan se aplica en el momento, no en tu renovación. Revisa el cambio de nuevo.',
		next: 'requote',
	},
	{
		match: 'PLAN_CHANGE_ADJUSTMENT_CLOSED',
		code: 'adjustment_closed',
		title: 'Este cobro ya no es válido',
		description: 'Pediste otro cambio o pasó el tiempo para pagarlo. Revisa el cambio de nuevo.',
		next: 'requote',
	},
	{
		match: 'PLAN_CHANGE_ADJUSTMENT_IN_FLIGHT',
		code: 'adjustment_in_flight',
		title: 'Tienes un pago en proceso',
		description: 'Tu banco todavía está revisando el pago de tu cambio anterior. Intenta de nuevo en unos minutos.',
		next: 'retry',
	},
	{
		match: 'A payment for this order is already in progress',
		code: 'payment_in_progress',
		title: 'Tu pago ya está en proceso',
		description: 'Espera unos segundos e inténtalo de nuevo.',
		next: 'retry',
	},
	{
		match: 'PLAN_CHANGE_CARD_ONLY',
		code: 'card_only',
		title: 'Este pago es solo con tarjeta',
		description: 'Paga con tarjeta de crédito o débito, en una sola exhibición.',
		next: 'retry',
	},
	{
		match: 'PLAN_CHANGE_CHARGE_NOT_AVAILABLE',
		code: 'charge_not_available',
		title: 'No pudimos preparar el cobro',
		description: 'Escríbenos y hacemos el cambio por ti.',
		next: 'support',
	},
	{
		match: ['PLAN_CHANGE_SUBSCRIPTION_PAUSED', 'está en pausa'],
		code: 'paused',
		title: 'Tu plan está en pausa',
		description: 'Reanúdalo y después cambia de plan.',
		next: 'none',
	},
	{
		match: ['PLAN_CHANGE_CANCELLATION_SCHEDULED', 'cancelación programada'],
		code: 'cancel_scheduled',
		title: 'Tu plan tiene la cancelación programada',
		description: 'Activa la renovación automática y después cambia de plan.',
		next: 'none',
	},
	{
		match: ['PLAN_CHANGE_RENEWAL_DECLINED', 'cobro de renovación rechazado'],
		code: 'failed_renewal',
		title: 'Tu último cobro no pasó',
		description: 'Actualiza tu tarjeta para pagar tu renovación y después cambia de plan.',
		next: 'none',
	},
	{
		match: 'cobro de la suscripción en curso',
		code: 'renewal_in_flight',
		title: 'Estamos cobrando tu renovación',
		description: 'Espera a que termine el cobro e inténtalo de nuevo en unos minutos.',
		next: 'retry',
	},
	{
		match: 'pago por adelantado en curso',
		code: 'prebilling_open',
		title: 'Tienes un pago adelantado pendiente',
		description: 'Termina o cierra tu pago adelantado y después cambia de plan.',
		next: 'none',
	},
	{
		match: 'periodos pagados por adelantado',
		code: 'prepaid_ahead',
		title: 'Este cambio lo hacemos por ti',
		description: 'Ya pagaste meses por adelantado. Escríbenos y hacemos el cambio por ti.',
		next: 'support',
	},
	{
		match: ['PLAN_CHANGE_PAID_BY_TRANSFER', 'se paga por transferencia'],
		code: 'pays_by_transfer',
		title: 'Este cambio lo hacemos por ti',
		description: 'Tu plan se paga por transferencia. Escríbenos y hacemos el cambio.',
		next: 'support',
	},
	{
		match: 'no encontramos el pago del periodo',
		code: 'period_payment_missing',
		title: 'Este cambio lo hacemos por ti',
		description: 'Necesitamos revisar el pago de tu periodo. Escríbenos y hacemos el cambio.',
		next: 'support',
	},
	{
		match: 'solo se cambia entre planes mensuales y anuales',
		code: 'periodicity_not_supported',
		title: 'Este cambio lo hacemos por ti',
		description: 'Tu plan no es mensual ni anual. Escríbenos y hacemos el cambio.',
		next: 'support',
	},
	{
		match: 'misma familia',
		code: 'family_change',
		title: 'Ese plan es de otro tipo',
		description: 'Puedes cambiar entre planes de declaraciones o entre planes de facturación, no de uno a otro.',
		next: 'none',
	},
	{
		match: 'ya tiene ese plan',
		code: 'same_plan',
		title: 'Ya tienes este plan',
		description: 'Elige otro plan si quieres cambiar.',
		next: 'none',
	},
	{
		match: 'ya terminó',
		code: 'subscription_ended',
		title: 'Tu plan ya no está activo',
		description: 'Para tener este plan, contrátalo de nuevo desde la página de planes.',
		next: 'none',
	},
	{
		match: 'no tiene un periodo vigente',
		code: 'no_current_period',
		title: 'Este cambio lo hacemos por ti',
		description: 'Escríbenos y revisamos tu plan para hacer el cambio.',
		next: 'support',
	},
	{
		match: 'plan nuevo no está disponible',
		code: 'target_unavailable',
		title: 'Ese plan no está disponible',
		description: 'Elige otro plan o escríbenos y te ayudamos.',
		next: 'support',
	},
	{
		match: 'no es de un plan que se pueda cambiar',
		code: 'not_changeable',
		title: 'Este cambio lo hacemos por ti',
		description: 'Tu plan no se cambia desde la app. Escríbenos y lo hacemos por ti.',
		next: 'support',
	},
];

const GENERIC_REFUSAL: PlanChangeRefusal = {
	code: 'unknown',
	title: 'No pudimos cambiar tu plan',
	description: 'Intenta de nuevo en unos minutos. Si sigue pasando, escríbenos y lo resolvemos.',
	next: 'support',
};

const engineAnswerOf = (error: unknown) => {
	const data = (error as { response?: { data?: { message?: unknown; error_code?: unknown } } })?.response?.data;
	const message = Array.isArray(data?.message) ? data.message.join(' ') : typeof data?.message === 'string' ? data.message : '';
	return { code: typeof data?.error_code === 'string' ? data.error_code : '', message };
};

/** The engine's `error_code` (when it sends one) and its message, as one text. */
const engineMessageOf = (error: unknown): string => {
	const { code, message } = engineAnswerOf(error);
	return code ? `${code}: ${message}` : message;
};

/** «tu renovación ya se está procesando; podrás…» → «Tu renovación ya se está procesando; podrás….» */
const sentenceOf = (message: string) => {
	const text = message.replace(/^PLAN_CHANGE_[A-Z_]+:\s*/, '').trim();
	return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}${/[.!?]$/.test(text) ? '' : '.'}` : '';
};

export const planChangeRefusalOf = (error: unknown): PlanChangeRefusal => {
	const { code, message } = engineAnswerOf(error);
	const text = engineMessageOf(error);
	const known =
		(code && REFUSALS.find((refusal) => [refusal.match].flat().includes(code))) ||
		REFUSALS.find((refusal) => [refusal.match].flat().some((fragment) => text.includes(fragment)));
	if (!known) return GENERIC_REFUSAL;
	const reason = known.engineReason ? sentenceOf(message) : '';
	return { code: known.code, title: known.title, description: reason || known.description, next: known.next };
};

/** 503 PLAN_CHANGE_NOT_ENABLED: the change is not open to this user yet. */
export const isPlanChangeNotEnabled = (error: unknown): boolean =>
	engineMessageOf(error).includes('PLAN_CHANGE_NOT_ENABLED');
